import { Component, OnDestroy, OnInit, inject } from "@angular/core";

import { Address } from "../../../Models/address";
import { AddressService } from "../../../Services/address.service";
import { Client } from "../../../Models/client";
import { AccountService } from "../../../Services/account.service";
import { WilayaItems } from "src/app/Services/wilayas.service";
import { Town } from "src/app/Models/town";
import { ProjectTheme } from "src/app/Models/ProjectTheme";
import { Subject, takeUntil } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { WilayaService } from "admin-common";
import { AdressGlobalService } from "src/app/shared/Services/adressGlobal.service";
import { LocalizedWilayaService } from "src/app/shared/Services/localized-wilaya.service";
import { LanguageService } from "src/app/Services/language.service";

@Component({
    selector: "app-address",
    templateUrl: "./address.component.html",
    styleUrls: ["./address.component.scss"],
    standalone: false,
    // the wilayas of the address popup (lib-adresse-popup) in the current language
    providers: [{ provide: WilayaService, useClass: LocalizedWilayaService }],
})
export class AddressComponent implements OnInit, OnDestroy {
  /**the app theme*/
  theme: ProjectTheme = "WUnivers";

  /**current language: in Arabic, the address popup refuses Latin letters in the names and the address*/
  language$ = inject(LanguageService).language$;

  //addresses list that comes from backedn before mapping, used to get the default address id from
  listAdddresses: Address[] = [];
  /**
   * list of client's addresses
   */
  addressList!: {
    id: number | undefined;
    fullName: string;
    phone: string;
    fullAddress: string;
  }[];

  /**ID of the client's default address*/
  // defaultAddressId!: number;

  /**
   * the selected address to be updates
   */
  selectedAddress!: any;

  /**id of the selected wilaya by user*/
  selectedWilayaId!: number;

  /**list of towns by selected wilaya*/
  townsList!: any[];

  /**current connected client object*/
  currentClient!: Client | null;

  /**subject used to unsubscribe from observables when destroying the component*/
  destroy$ = new Subject<void>();

  /**
   * snackbar component params object
   */
  showSnackbar: boolean = false;
  snackbarParams: { theme: string; message: string } = {
    theme: "",
    message: "",
  };

  defaultAddressId!: number;

  constructor(
    private addressService: AddressService,
    private accountService: AccountService,
    private wilayaService: WilayaItems,
    private translate: TranslateService,
    private adressGlobalService: AdressGlobalService,
  ) {}

  ngOnInit(): void {
    /**Get connected client Object*/
    this.accountService.accountSubject
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (client: Client | null) => {
          this.currentClient = client;
          this.getListAddressesByClientId();
        },
        error: (error) => {
          console.error(error);
        },
      });
  }

  /**
   * Centralized snackbar method with auto hide
   */
  private openSnackbar(
    theme: string,
    message: string,
    duration: number = 3000,
  ): void {
    this.snackbarParams = { theme, message };
    this.showSnackbar = true;

    setTimeout(() => {
      this.showSnackbar = false;
    }, duration);
  }

  /**
   * get the selected address to be updated
   */
  getAdressToUpdate(adrId: number): void {
    const adress = this.listAdddresses.find((a) => a.id === adrId);
    if (!adress) return;
    this.selectedAddress = {
      id: adress.id,
      firstName: adress.firstname,
      lastName: adress.lastname,
      phone: adress.phoneNumber,
      adresse: adress.addressLine1,
      wilaya: adress.province,
      commune: adress.city,
      default: adress.primary,
    };
  }

  /**
   * update a client address
   */
  onUpdateAddress(data: any): void {
    if (this.currentClient?.id) {
      const address: Address = {
        id: data.id,
        clientId: this.currentClient?.id,
        country: "",
        firstname: data.firstName,
        lastname: data.lastName,
        addressLine1: data.adresse,
        addressLine2: "",
        city: data.commune,
        province: data.wilaya,
        zipCode: "",
        phoneNumber: data.phone,
        type: data.type,
        primary: data.default,
      };

      if (this.defaultAddressId === address.id && !address.primary) {
        const message = this.translate.instant(
          "account.address.changeDefaultNotAllowed",
        );
        this.openSnackbar("warning", message);
        return;
      }

      if (!(address.primary && this.defaultAddressId !== address.id)) {
        if (this.addressExists(address)) {
          const message = this.translate.instant("account.address.alreadyExists");
          this.openSnackbar("warning", message);
          return;
        }
      }

      // previous default (before the update)
      const previousDefaultId = this.defaultAddressId;
      const replacesExistingDefault =
        !!address.primary &&
        !!previousDefaultId &&
        previousDefaultId !== address.id;

      this.addressService
        .updateAdr(address)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            if (replacesExistingDefault) {
              const message = this.translate.instant(
                "account.address.updateSuccessSingleDefault",
              );
              this.openSnackbar("warning", message);
            } else {
              this.openSnackbar(
                "success",
                this.translate.instant("account.address.updateSuccess"),
              );
            }
            this.getListAddressesByClientId();
          },
          error: (err) => {
            const backendMsg = err?.error?.message || "";
            if (backendMsg.includes("entity.already.exists")) {
              const message = this.translate.instant(
                "account.address.alreadyExists",
              );
              this.openSnackbar("alert", message);
              return;
            }
            this.openSnackbar(
              "alert",
              this.translate.instant("account.address.updateError"),
            );
          },
        });
    }
  }

  /**
   * delete a client's address
   */
  onDeleteAddress(adrId: number): void {
    this.getAdressToUpdate(adrId);

    this.addressService
      .remove(this.selectedAddress)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.getListAddressesByClientId();
          this.openSnackbar(
            "success",
            this.translate.instant("account.address.deleteSuccess"),
          );
        },
        error: () => {
          this.openSnackbar(
            "alert",
            this.translate.instant("account.address.deleteError"),
          );
        },
      });
  }

  /**
   * Create a new address
   */
  onCreateAddress(data: any): void {
    if (!this.currentClient?.id) {
      return;
    }
    const address = {
      clientId: this.currentClient?.id,
      country: "",
      firstname: data.firstName,
      lastname: data.lastName,
      addressLine1: data.adresse,
      addressLine2: "",
      city: data.commune,
      province: data.wilaya,
      zipCode: "",
      phoneNumber: data.phone,
      type: data.type,
      primary: data.default,
    };

    if (this.addressExists(address)) {
      const message = this.translate.instant("account.address.alreadyExists");
      this.openSnackbar("warning", message);
      return;
    }

    // the client already has a default address and the new one replaces it
    const replacesExistingDefault =
      !!address.primary && !!this.defaultAddressId;

    this.addressService
      .addAddress(address)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          if (replacesExistingDefault) {
            const message = this.translate.instant(
              "account.address.addSuccessSingleDefault",
            );
            this.openSnackbar("warning", message);
          } else {
            this.openSnackbar(
              "success",
              this.translate.instant("account.address.addSuccess"),
            );
          }
          this.getListAddressesByClientId();
        },
        error: (err) => {
          const backendMsg = err?.error.message || "";
          if (backendMsg.includes("entity.already.exists")) {
            const message = this.translate.instant(
              "account.address.alreadyExists",
            );
            this.openSnackbar("alert", message);
            return;
          }
          this.openSnackbar(
            "alert",
            this.translate.instant("account.address.addError"),
          );
        },
      });
    this.scrollToTheTop();
  }

  /**get the list of addresses by client id*/
  getListAddressesByClientId(): void {
    if (this.currentClient?.id) {
      this.addressService
        .getByClientId(this.currentClient.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (listAddresses: Address[]) => {
            this.listAdddresses = listAddresses;
            //update default address id
            this.defaultAddressId =
              this.listAdddresses.find((item) => item.primary === true)?.id ??
              0;
            this.addressList = listAddresses.map((address: Address) => ({
              id: address.id,
              fullName: `${address.firstname} ${address.lastname}`,
              phone: address.phoneNumber,
              default: address.primary,
              fullAddress: [
                address.addressLine1?.trim(),
                address.addressLine2?.trim(),
                address.city?.trim(),
                // shown only: the wilaya name in the current language (LAGHOUAT -> Laghouat / الأغواط)
                address.province &&
                  this.adressGlobalService.getWilayaLabel(address.province.trim()),
              ]
                .filter((value) => value) // removes empty, null, undefined, ""
                .join(", "),
            }));
          },
          error: (error) => {
            console.error(error);
          },
        });
      this.scrollToTheTop();
    }
  }

  /**
   * select a wilaya
   */
  onSelectWilaya(wilaya: string) {
    const wilayaItem = this.wilayaService
      .getWilayasItemss()
      .find((w) => w.value === wilaya);

    this.selectedWilayaId = Number(wilayaItem?.id);
    this.setTownsList();
  }

  /**
   * set the list of towns
   */
  setTownsList() {
    this.addressService
      .getTownsByWilayaId(this.selectedWilayaId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (towns: Town[]) => {
          this.townsList = towns.map((town) => ({
            label: town.label || town.name,
            value: town.name,
          }));
        },
        error: (error) => {
          console.error(error);
        },
      });
  }

  /**
   * scroll to the top of the page for a better UX
   */
  private scrollToTheTop(): void {
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
  }

  /**
   * Checks if an address already exists in the list.
   * Two addresses are considered the same when the client id, first name,
   * last name, phone number, address line 1, city and province all match.
   * The default (primary) flag is ignored, and the address being updated
   * is not compared with itself.
   */
  private addressExists(adr: Address): boolean {
    return this.listAdddresses.some(
      (item) =>
        item.id !== adr.id &&
        item.clientId === adr.clientId &&
        item.firstname === adr.firstname &&
        item.lastname === adr.lastname &&
        item.phoneNumber === adr.phoneNumber &&
        item.addressLine1 === adr.addressLine1 &&
        item.city === adr.city &&
        item.zipCode === adr.zipCode &&
        item.province === adr.province,
    );
  }

  /**
   * lifecycle hook onDestroy
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
